import { Environment } from '@/utils/environment/environment.interface';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, type Observable } from 'rxjs';
import { type CoreApiToken, CoreApiVersionPath } from '../../../shared';
import { RosaCoreApiService } from '../core-api.service';

@Injectable()
export class RosaCoreApiAuthService {
    private _coreApiService = inject(RosaCoreApiService);
    private _env = inject(Environment);

    getToken(email: string, password: string): Observable<CoreApiToken> {
        const basicAuth = this._env.advizeoClientId + ':' + this._env.advizeoClientSecret;

        const formData = new FormData();
        formData.append('username', email);
        formData.append('password', password);
        formData.append('grant_type', 'password');

        return this._coreApiService.post('oauth/token', formData, { Authorization: `Basic ${btoa(basicAuth)}` }, true);
    }

    async getTokenForConnectedAs(email: string, projectId?: string): Promise<any> {
        const queryParams = projectId ? `?projectId=${projectId}` : '';
        return await firstValueFrom(this._coreApiService.get(`${CoreApiVersionPath.V3}oauth/connect-as/${email}${queryParams}`));
    }

    async getTokenForConnectedAsFromSpecificToken(token: string, email: string, projectId?: string): Promise<any> {
        const queryParams = projectId ? `?projectId=${projectId}` : '';
        return await firstValueFrom(
            this._coreApiService.get(
                `${CoreApiVersionPath.V3}oauth/connect-as/${email}${queryParams}`,
                {},
                {
                    Authorization: `Bearer ${token}`
                },
                true
            )
        );
    }

    resetPassword(email: string, branding: string): Promise<void> {
        return firstValueFrom(this._coreApiService.get<void>('accounts/requestResetPassword', { email, branding }, null, true));
    }

    switchProjectInToken(projectId: string): Promise<CoreApiToken> {
        return firstValueFrom(this._coreApiService.get<CoreApiToken>(`${CoreApiVersionPath.V3}me/projects/switch-to/${projectId}`));
    }

    convertIdpAccessTokenToAdvizeoAuthToken(
        client: string,
        idpAccessToken: string | null,
        idpCode: string | null,
        codeVerifier: string | null,
        projectId: string | null
    ): Promise<CoreApiToken> {
        const params = new URLSearchParams();
        params.append('client', client);
        params.append('rootUri', window.location.hostname);

        if (isNotNullish(idpAccessToken)) {
            params.append('token', idpAccessToken);
        }
        if (isNotNullish(idpCode)) {
            params.append('code', idpCode);
        }
        if (isNotNullish(codeVerifier)) {
            params.append('codeVerifier', codeVerifier);
        }
        if (isNotNullish(projectId)) {
            params.append('projectId', projectId);
        }

        return firstValueFrom(this._coreApiService.get<CoreApiToken>(`oauth/advizeo-token?${params.toString()}`, null, null, true));
    }
}
